import React, { useCallback, useMemo, useState } from "react";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import ExerciseLibraryLine from "./ExerciseLibraryLine";
import SvgIcon from "../../../parts/SvgIcon";
import PrimaryDropdown from "../../../parts/primary-dropdown";
import submit from "submit-form";
import ToolTip from "../../../parts/tooltip";

const MOVEMENTS = {
  squat: "Squat",
  bench: "Bench",
  deadlift: "Deadlift",
  clean: "Clean",
  jerk: "Jerk",
  snatch: "Snatch",
  overheadPress: "Overhead Press",
};

const exerciseOptions = {
  squat: "Squat Variation",
  bench: "Bench Variation",
  deadlift: "Deadlift Variation",
  clean: "Clean Variation",
  jerk: "Jerk Variation",
  snatch: "Snatch Variation",
  overheadPress: "Overhead Press Variation",
  other: "Other Variation",
};

function ExerciseLibraryCoachComponent({
  type,
  primary,
  secondaries,
  activeUserToRequest,
  isAdminConsole = false,
}) {
  const [search, setSearch] = useState("");

  const handleSearch = useCallback(
    (event) => setSearch(event.target.value),
    []
  );

  const exercises = useMemo(() => {
    if (!secondaries) return [];
    if (!search) return secondaries;

    return secondaries.filter(({ movement }) => {
      return String(movement)
        .toLowerCase()
        .includes(search.toLowerCase().trim());
    });
  }, [secondaries, search]);

  const showPrimary = useMemo(
    () =>
      !["assistant", "other"].includes(primary) &&
      !isAdminConsole &&
      (!search ||
        (search &&
          primary.toLowerCase().includes(search.toLowerCase().trim()))),
    [primary, search, isAdminConsole]
  );

  const primaryLabel = exerciseOptions[primary] || "Accessory";

  const primaryOption = {
    label: `Add ${primaryLabel}`,
    value: primary || "assistant",
  };

  const dropdownOptions = [
    primaryOption,
    ...Object.entries(exerciseOptions).map(([key, label]) => ({
      label: `Add ${label}`,
      value: key,
    })),
    { label: "Add Accessory", value: "assistant" },
  ].filter(
    (option, index, self) =>
      self.findIndex((o) => o.label === option.label) === index
  );

  const handleOpenModal = useCallback((value) => {
    if (value) {
      const body =
        value !== "assistant"
          ? {
              ["modal[type]"]: "add-secondary-movement",
              ["modal[primary]"]: value,
            }
          : {
              ["modal[type]"]: "add-other-exercise",
            };

      submit("/actions/refresh", {
        method: "GET",
        body,
      });
    }
  }, []);

  return (
    <main className="ex-library-wrapper">
      <header>
        <hgroup className="mobile-hidden">
          <h3>{isAdminConsole ? "master" : ""} Exercise library</h3>
          {!isAdminConsole && (
            <p className="p4">
              Build and manage the master exercise library for your account so
              you can build training programs
            </p>
          )}
        </hgroup>

        <hgroup>
          {!isAdminConsole && (
            <a
              role="button"
              data-button-type="outline"
              href="/coaching-portal/exercise-library/catalog"
            >
              Add from catalog
            </a>
          )}
          <PrimaryDropdown
            summaryText={"Add Custom Exercise"}
            options={dropdownOptions}
            onSelectFn={handleOpenModal}
            fullOption={false}
            alignRight={true}
            isAdminConsole={isAdminConsole}
          />
        </hgroup>
      </header>

      <section>
        <div>
          <input
            type="search"
            name="search"
            placeholder="Search Exercise"
            value={search}
            onChange={handleSearch}
            autoComplete="off"
            data-expandable
            className={search.length ? "expanded" : ""}
          />
          <div className="flex items-center">
            <hgroup>
              <p className="mb-2">Custom Exercise</p>
              <ToggleLinksInput
                className="tabs inline-tabs"
                name="primary"
                options={[{ text: "Accessories", value: "assistant" }]}
              />
            </hgroup>
            <hgroup>
              <div className="row">
                <p>Powerlifting</p>
                <ToolTip
                  id="powerlifting"
                  body="Powerlifting primary exercises and variations can each have their own 1 rep max and be programmed as a % of 1RM"
                >
                  <span className="tooltip-icon">?</span>
                </ToolTip>
              </div>
              <ToggleLinksInput
                className="tabs inline-tabs"
                name="primary"
                options={[
                  { text: "Squat", value: "squat" },
                  { text: "Bench", value: "bench" },
                  { text: "Deadlift", value: "deadlift" },
                ]}
              />
            </hgroup>
            <hgroup>
              <div className="row">
                <p>Olympic Weightlifting</p>
                <ToolTip
                  id="olympic-weightlifting"
                  body="Olympic Weightlifting primary exercises and variations can each have their own 1 rep max and be programmed as a % of 1RM"
                >
                  <span className="tooltip-icon">?</span>
                </ToolTip>
              </div>
              <ToggleLinksInput
                className="tabs inline-tabs"
                name="primary"
                options={[
                  { text: "Clean", value: "clean" },
                  { text: "Jerk", value: "jerk" },
                  { text: "Snatch", value: "snatch" },
                ]}
              />
            </hgroup>
            <hgroup>
              <div className="row">
                <p>Strongman & other</p>
                <ToolTip
                  id="strongman"
                  body='Strongman & other primary exercises and variations can each have their own 1 rep max and be programmed as a % of 1RM. Use the "other" category to include anything such as Strongman events, Streetlifting exercises or any exercise you want to program loads using a % of 1 rep max'
                >
                  <span className="tooltip-icon">?</span>
                </ToolTip>
              </div>
              <ToggleLinksInput
                className="tabs inline-tabs"
                name="primary"
                options={[
                  { text: "Overhead Press", value: "overheadPress" },
                  { text: "Other", value: "other" },
                ]}
              />
            </hgroup>
          </div>
        </div>
        <section
          role="table-wrapper"
          data-scrollable
          className="table-exercise-library"
        >
          <table>
            <thead>
              <tr>
                <th />
                <th>Exercise Name</th>
                <th>Video</th>
                <th>Instruction</th>
                <th>Primary muscle</th>
                <th>Secondary muscle</th>
                <th>Exercise type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {showPrimary && (
                <ExerciseLibraryLine
                  secondary={Object.assign(
                    { movement: MOVEMENTS[primary], primary },
                    activeUserToRequest.primaryExercises?.[primary]
                  )}
                  primaryItem
                />
              )}
              {exercises?.length ? (
                <>
                  {exercises.map((secondary, i) => {
                    return (
                      <ExerciseLibraryLine
                        secondary={secondary}
                        key={`${secondary.primary}_${i}`}
                        isAdminConsole={isAdminConsole}
                        primary={primary}
                      />
                    );
                  })}
                </>
              ) : null}
            </tbody>
          </table>
          {(!exercises || exercises.length === 0) && !showPrimary && (
            <EmptyView search={search} />
          )}
          <div
            className="dashed-box add-new-exercise force-mobile-hidden"
            onClick={() => {
              handleOpenModal(primary);
            }}
          >
            <SvgIcon src="/img/new-icons/add-square-outline.svg" alt="back" />
            <span>Add new exercise</span>
          </div>
        </section>
      </section>
    </main>
  );
}

function EmptyView({ search }) {
  return (
    <div className="empty-state">
      <p className="s1">
        No exercises {search ? "with that name " : " "}have been found
      </p>
    </div>
  );
}

export default ExerciseLibraryCoachComponent;
